import {
  CalendarOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  KeyOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { AppLayout } from '../components/AppLayout';
import { AssignSpeakerToEventsModal } from '../components/AssignSpeakerToEventsModal';
import { PageHead } from '../components/PageHead';
import { SetPasswordModal } from '../components/SetPasswordModal';
import { SpeakerActivityDrawer } from '../components/SpeakerActivityDrawer';
import { api, type SpeakerRow } from '../lib/api';
import { openSpeakerPreview } from '../lib/preview';

const { Title } = Typography;

export function SpeakersScene() {
  const [speakers, setSpeakers] = useState<SpeakerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<{ name: string; email: string }>();
  const [pwdTarget, setPwdTarget] = useState<SpeakerRow | null>(null);
  const [assignTarget, setAssignTarget] = useState<SpeakerRow | null>(null);
  const [activityTarget, setActivityTarget] = useState<SpeakerRow | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      setSpeakers(await api<SpeakerRow[]>('/api/speakers'));
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to load speakers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const onAdd = async (values: { name: string; email: string }) => {
    setAdding(true);
    try {
      await api('/api/speakers', {
        method: 'POST',
        body: JSON.stringify(values),
      });
      form.resetFields();
      setAddOpen(false);
      await load();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to add speaker');
    } finally {
      setAdding(false);
    }
  };

  const clearPassword = async (speakerId: string) => {
    try {
      await api(`/api/speakers/${speakerId}/password`, { method: 'DELETE' });
      await load();
      message.success('Password cleared');
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to clear password');
    }
  };

  const remove = async (speakerId: string) => {
    try {
      // The Worker returns { alsoTeamMember: boolean } so we can
      // reassure the user that their team admin access is untouched
      // when the deleted speaker shares an email with a team_members
      // row. Same-email overlap was previously erroring out because
      // the cascade wasn't cleaning speaker_activity / invites.
      const result = await api<{ alsoTeamMember?: boolean }>(
        `/api/speakers/${speakerId}`,
        { method: 'DELETE' },
      );
      await load();
      if (result?.alsoTeamMember) {
        message.success('Speaker removed. Their team admin account is unchanged.');
      } else {
        message.success('Speaker removed.');
      }
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to remove speaker');
    }
  };

  const columns: ColumnsType<SpeakerRow> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    {
      title: 'Password',
      key: 'password',
      width: 140,
      render: (_t, row) =>
        row.has_password ? <Tag color="green">Set</Tag> : <Tag>None</Tag>,
    },
    {
      title: 'Added',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (ts: number) => dayjs(ts).format('D MMM YYYY'),
    },
    {
      title: '',
      key: 'actions',
      width: 220,
      render: (_t, row) => (
        <Space>
          <Button
            size="small"
            icon={<ExperimentOutlined />}
            onClick={async () => {
              try {
                await openSpeakerPreview(row.id);
              } catch (err) {
                message.error((err as Error).message);
              }
            }}
            title="Open the speaker view in a new tab without signing out"
          >
            Preview
          </Button>
          <Button
            size="small"
            icon={<ClockCircleOutlined />}
            onClick={() => setActivityTarget(row)}
          >
            Activity
          </Button>
          <Button
            size="small"
            icon={<CalendarOutlined />}
            onClick={() => setAssignTarget(row)}
          >
            Events
          </Button>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => setPwdTarget(row)}
          >
            {row.has_password ? 'Reset password' : 'Set password'}
          </Button>
          {row.has_password && (
            <Popconfirm
              title="Clear this speaker's password?"
              okText="Clear"
              onConfirm={() => clearPassword(row.id)}
            >
              <Button size="small">Clear</Button>
            </Popconfirm>
          )}
          <Popconfirm
            title="Delete this speaker?"
            description="They'll be removed from every event they're on."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => remove(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHead title="Speakers" />
      <Space
        align="center"
        style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Speakers
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
          Add speaker
        </Button>
      </Space>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={speakers}
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: 'No speakers yet.' }}
        />
      </Card>

      <Modal
        title="Add speaker"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={adding}
        okText="Add"
      >
        <Form form={form} layout="vertical" onFinish={onAdd}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input autoFocus />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter an email' },
              { type: 'email', message: 'Not a valid email' },
            ]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <SetPasswordModal
        open={pwdTarget !== null}
        speakerId={pwdTarget?.id ?? null}
        speakerName={pwdTarget?.name ?? ''}
        onClose={() => setPwdTarget(null)}
        onDone={load}
      />

      <AssignSpeakerToEventsModal
        open={assignTarget !== null}
        speaker={assignTarget}
        onClose={() => setAssignTarget(null)}
        onDone={load}
      />

      <SpeakerActivityDrawer
        open={activityTarget !== null}
        speaker={activityTarget}
        onClose={() => setActivityTarget(null)}
      />
    </AppLayout>
  );
}
